import axios, { AxiosInstance } from 'axios';

const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

function createClient(getToken: () => Promise<string | null>, anonSessionId: string | null): AxiosInstance {
  const client = axios.create({ baseURL: BASE_URL });
  client.interceptors.request.use(async (config) => {
    const token = await getToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    } else if (anonSessionId) {
      config.headers['X-Session-ID'] = anonSessionId;
    }
    return config;
  });
  return client;
}

export { createClient };

// ── typed API helpers ─────────────────────────────────────────────────────────

export interface FileRecord {
  file_id: string;
  file_name: string;
  sheets: SheetMeta[];
  total_tables: number;
  total_formulas: number;
  dependency_graph: Record<string, string[]>;
  created_at: number;
  shared_with_org?: string;
}

export interface SheetMeta {
  sheet_id: string;
  sheet_name: string;
  sheet_index: number;
  table_ids: string[];
}

export interface TableMeta {
  table_id: string;
  file_id: string;
  sheet_id: string;
  start_row: number;
  end_row: number;
  start_col: string;
  end_col: string;
  headers: string[];
  row_count: number;
  col_count: number;
  columns: ColumnMeta[];
  parquet_key: string;
  dependencies: string[];
}

export interface ColumnMeta {
  column_name: string;
  data_type: string;
  null_count: number;
  unique_count: number;
  sample_values: string[];
}

export interface QueryPlan {
  query: string;
  query_id: string;
  tables_involved: string[];
  operations: Operation[];
  expected_output_format: string;
  visualization: string;
}

export interface Operation {
  operation_type: string;
  table_id: string;
  description: string;
  parameters: Record<string, unknown>;
  sequence_order: number;
}

export interface QueryResult {
  type: 'dataframe' | 'scalar';
  data: Record<string, unknown>[] | string;
  columns?: string[];
  rows?: number;
}

export interface Session {
  session_id: string;
  name: string;
  file_ids: string[];
  created_at: number;
  updated_at: number;
}

export interface OrgRecord {
  org_id: string;
  name: string;
  description: string;
  owner_id: string;
  created_at: number;
}

export interface Member {
  org_id: string;
  user_id: string;
  role: string;
  email: string;
  name: string;
  joined_at: number;
}

export interface Activity {
  activity_id: string;
  org_id: string;
  user_id: string;
  user_name: string;
  action: string;
  resource_type: string;
  resource_id: string;
  details: string;
  created_at: number;
}
